import { invertObject, NameAnd } from "@me8eon/record_utils";
import { SearchTextFilter, searchTextName } from "@me8eon/searchtext_filters";
import { makeContextFor } from "@me8eon/context";
import { useCallback } from "react";
import { useGuiFilters } from "@me8eon/filters";
import { useTriggerSearch } from "./search";
import { useSelectedDataViewName } from "@me8eon/data_view_filter";
import { BooleanFeatureFlag } from "@me8eon/featureflags";
import { useDebug } from "@me8eon/debug";
import { searchDebugName } from "@me8eon/filter_data_widget";
import { dataViewFilterName } from "@me8eon/data_views";

export const searchParserFFName = "searchParser";
export const searchParserFeature: BooleanFeatureFlag = {
    value: false,
    description: "Search Parser / Intent Parser",
};
export type SearchParserConfig = {
    /** this is from data view name to key words that are about that data view
     *    mygenius: ['training','mygenius'],
     *    jira: 'jira',
     */
    indexWords: NameAnd<string | string[]>
    //For example ['from', 'in']
    fromWords: string[]
    //For example ['what', 'why', 'how']
    questionWords: string[]
    //for example ['lucy', 'ai']
    aiNames: string[]
    orderWords: string[]
}

export type SearchParserResult<Filters extends SearchTextFilter> = {
    filters: Filters
}

export type SearchParserMaker<Filters extends SearchTextFilter> = (config: SearchParserConfig) => SearchParserFn<Filters>
export type SearchParserFn<Filters extends SearchTextFilter> = (filters: Filters) => SearchParserResult<Filters> | undefined

export function composeSearchParserFn<Filters extends SearchTextFilter>(...parsers: SearchParserMaker<Filters>[]): SearchParserMaker<Filters> | undefined {
    return (config) => (filters) => {
        for (const parser of parsers) {
            const result = parser(config)(filters);
            if (result) {
                return result;
            }
        }
        return undefined;
    };
}

export const inSourceFilter = <Filters extends SearchTextFilter>(): SearchParserMaker<Filters> =>
    (config: SearchParserConfig) => {
        const keyWordToDataView = invertObject(config.indexWords);
        const keys = new Set<string>(Object.keys(keyWordToDataView));

        return (filters: Filters): SearchParserResult<Filters> | undefined => {
            const searchText = filters[searchTextName];
            const words = searchText.trim().split(/\s+/);
            const lastButOneWord = words[words.length - 2];
            const lastWord = words[words.length - 1];
            if (lastWord && config.fromWords.includes(lastButOneWord || ""))
                if (keys.has(lastWord)) {
                    const dataView = keyWordToDataView[lastWord];
                    return { filters: { ...filters, [searchTextName]: words.slice(0, -2).join(" "), [dataViewFilterName]: dataView } };
                }
            return undefined;
        };
    };

export const aiAssistantFilter = <Filters extends SearchTextFilter>(): SearchParserMaker<Filters> => {
    return config => filters => {
        const searchText = filters[searchTextName];
        const words = searchText.trim().split(/\s+/);
        const firstWord = words[0];
        if (config.questionWords.includes(firstWord)) {
            return { filters: { ...filters, [dataViewFilterName]: "aiAssistant" } };
        }
    };
};

export const orderFilter = <Filters extends SearchTextFilter>(): SearchParserMaker<Filters> => {
    return config => filters => {
        const searchText = filters[searchTextName];
        const words = searchText.trim().split(/\s+/);
        const firstWord = words[0];
        if (config.orderWords.includes(firstWord)) {
            return { filters: { ...filters, [searchTextName]: words.slice(1).join(" "), [dataViewFilterName]: "orderit" } };
        }
    };
};
export const aiPrefixAssistantFilter = <Filters extends SearchTextFilter>(): SearchParserMaker<Filters> => {
    return config => filters => {
        const searchText = filters[searchTextName];
        const words = searchText.trim().split(/\s+/);
        const firstWord = words[0];
        if (config.aiNames.includes(firstWord)) {
            return {
                filters: {
                    ...filters,
                    [searchTextName]: words.slice(1).join(" "),
                    [dataViewFilterName]: "aiAssistant",
                },
            };
        }
    };
};

export const alwaysMatchDefaultFilter = <Filters extends SearchTextFilter>(): SearchParserMaker<Filters> =>
    config => filters => ({ filters });

export const { use: useSearchParserMaker, Provider: SearchParserMakerProvider } = makeContextFor<SearchParserMaker<any>, "searchParserMaker">("searchParserMaker",
    composeSearchParserFn(
        inSourceFilter(),
        aiAssistantFilter(),
        aiPrefixAssistantFilter(),
        orderFilter(),
        alwaysMatchDefaultFilter()));

export const { use: useSearchParserConfig, Provider: SearchParserConfigProvider } = makeContextFor<SearchParserConfig, "searchParserConfig">("searchParserConfig");

export function useSearchParser<Filter extends SearchTextFilter>(): SearchParserFn<Filter> {
    const parserMaker = useSearchParserMaker();
    const config = useSearchParserConfig();
    return useCallback(parserMaker(config), [parserMaker, config]);
}

export function useSetSearchText<Filters extends SearchTextFilter>(st: string): (s: string) => void {
    const parser = useSearchParser<Filters>();
    const [guiFilters, setGuiFilters] = useGuiFilters();
    const trigger = useTriggerSearch();
    const [selectedDataViewName, setSelectedDataViewName] = useSelectedDataViewName();
    const debug = useDebug(searchDebugName);
    return s => {
        const filters: Filters = { ...guiFilters, [searchTextName]: s } as any;
        const result = parser(filters)!;
        const { filters: newFilters } = result;
        debug("useSetSearchText", filters, result, newFilters);
        setGuiFilters(newFilters);
        trigger(st, newFilters);
    };
}
